"use client";

import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface OrderStepsCardProps {
  data: {
    question: string;
    steps: string[];
    correctOrder: number[];
    explanation: string;
  };
  onAnswer: (correct: boolean) => void;
}

function shuffleIndices(length: number): number[] {
  const indices = Array.from({ length }, (_, i) => i);
  // Fisher-Yates shuffle with a simple seed derived from the array length
  // to ensure a deterministic-per-mount but shuffled result
  let seed = length * 2654435761;
  for (let i = indices.length - 1; i > 0; i--) {
    seed = (seed ^ (seed << 13)) >>> 0;
    seed = (seed ^ (seed >> 17)) >>> 0;
    seed = (seed ^ (seed << 5)) >>> 0;
    const j = seed % (i + 1);
    const tmp = indices[i]!;
    indices[i] = indices[j]!;
    indices[j] = tmp;
  }
  return indices;
}

export function OrderStepsCard({ data, onAnswer }: OrderStepsCardProps) {
  const { question, steps, correctOrder, explanation } = data;
  const [currentOrder, setCurrentOrder] = useState<number[]>([]);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setCurrentOrder(shuffleIndices(steps.length));
  }, [steps]);

  function moveUp(position: number) {
    if (submitted || position === 0) return;
    setCurrentOrder((prev) => {
      const next = [...prev];
      const tmp = next[position - 1]!;
      next[position - 1] = next[position]!;
      next[position] = tmp;
      return next;
    });
  }

  function moveDown(position: number) {
    if (submitted || position === currentOrder.length - 1) return;
    setCurrentOrder((prev) => {
      const next = [...prev];
      const tmp = next[position]!;
      next[position] = next[position + 1]!;
      next[position + 1] = tmp;
      return next;
    });
  }

  function handleCheck() {
    setSubmitted(true);
    const allCorrect = currentOrder.every(
      (stepIndex, position) => stepIndex === correctOrder[position]
    );
    onAnswer(allCorrect);
  }

  function isPositionCorrect(position: number): boolean {
    return currentOrder[position] === correctOrder[position];
  }

  if (currentOrder.length === 0) return null;

  return (
    <div className="flex flex-col gap-6">
      <p className="text-lg font-medium mb-4">{question}</p>

      <div className="flex flex-col gap-2">
        {currentOrder.map((stepIndex, position) => (
          <div
            key={stepIndex}
            className={cn(
              "rounded-lg border p-3 flex items-center gap-3 transition",
              submitted && isPositionCorrect(position) &&
                "border-l-4 border-green-500 bg-green-50",
              submitted && !isPositionCorrect(position) &&
                "border-l-4 border-red-500 bg-red-50"
            )}
          >
            <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-sm font-semibold shrink-0">
              {position + 1}
            </div>

            <span className="flex-1 text-sm">{steps[stepIndex]}</span>

            <div className="flex flex-col gap-0.5">
              <button
                type="button"
                disabled={submitted || position === 0}
                onClick={() => moveUp(position)}
                className={cn(
                  "w-7 h-7 flex items-center justify-center rounded text-xs transition",
                  submitted || position === 0
                    ? "opacity-30 cursor-default"
                    : "hover:bg-muted cursor-pointer"
                )}
                aria-label={`Move "${steps[stepIndex]}" up`}
              >
                <svg width="10" height="8" viewBox="0 0 10 8" fill="currentColor">
                  <polygon points="5,0 10,8 0,8" />
                </svg>
              </button>
              <button
                type="button"
                disabled={submitted || position === currentOrder.length - 1}
                onClick={() => moveDown(position)}
                className={cn(
                  "w-7 h-7 flex items-center justify-center rounded text-xs transition",
                  submitted || position === currentOrder.length - 1
                    ? "opacity-30 cursor-default"
                    : "hover:bg-muted cursor-pointer"
                )}
                aria-label={`Move "${steps[stepIndex]}" down`}
              >
                <svg width="10" height="8" viewBox="0 0 10 8" fill="currentColor">
                  <polygon points="0,0 10,0 5,8" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      {submitted && (
        <div className="bg-muted rounded-lg p-4 mt-4">
          <p className="font-medium mb-2">Correct order:</p>
          <ol className="list-decimal list-inside text-sm mb-3 space-y-1">
            {correctOrder.map((stepIndex, i) => (
              <li key={i}>{steps[stepIndex]}</li>
            ))}
          </ol>
          <p className="font-medium mb-1">Explanation</p>
          <p className="text-sm leading-relaxed">{explanation}</p>
        </div>
      )}

      {!submitted && (
        <Button className="w-full" onClick={handleCheck}>
          Check
        </Button>
      )}
    </div>
  );
}
